import { useEffect, useMemo, useState } from "react";
import {
  beginLogin,
  completeLogin,
  loadPortalContext,
  loadRuntimeConfig,
  signOut,
  type PortalSession,
  type RuntimeConfig,
} from "./auth";
import { documents, fixtureRelease } from "./data";

const navigation = [
  "Overview",
  "Downloads",
  "Documentation",
  "Licenses",
  "Organization",
  "Billing",
  "Support",
] as const;
type NavigationItem = (typeof navigation)[number];

function StatusPill({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "warning" | "ok";
}) {
  return <span className={`status status-${tone}`}>{children}</span>;
}

function Overview() {
  return (
    <>
      <section className="hero" aria-labelledby="overview-title">
        <div>
          <p className="eyebrow">AIR GAP INTEL // CUSTOMER PORTAL</p>
          <h1 id="overview-title">BlackBox Analyst operations center</h1>
          <p className="lede">
            Manage authorized releases, offline update bundles, documentation,
            licenses, and organization access from one controlled location.
          </p>
        </div>
        <div className="instrument" aria-label="Development environment status">
          <span className="instrument-light" aria-hidden="true" />
          <div>
            <strong>Development fixture</strong>
            <span>No customer system or real installer connected</span>
          </div>
        </div>
      </section>

      <section className="metric-grid" aria-label="Organization summary">
        <article className="metric-card">
          <span>Organization</span>
          <strong>Air Gap Intel Development</strong>
          <small>Fixture tenant</small>
        </article>
        <article className="metric-card">
          <span>Release channel</span>
          <strong>Development</strong>
          <small>Customer promotion disabled</small>
        </article>
        <article className="metric-card">
          <span>Entitlement</span>
          <strong>Not issued</strong>
          <small>License signing not connected</small>
        </article>
      </section>

      <section className="panel split-panel" aria-labelledby="foundation-title">
        <div>
          <p className="section-tag">CONTROL PLANE FOUNDATION</p>
          <h2 id="foundation-title">Local operation stays local</h2>
          <p>
            The portal manages software distribution and administration. Packet
            captures, findings, investigations, and local model activity do not
            enter this platform by default.
          </p>
        </div>
        <ol className="trace-list">
          <li>
            <span>01</span> Release candidate created
          </li>
          <li>
            <span>02</span> Signatures and checks verified
          </li>
          <li>
            <span>03</span> Human promotion approved
          </li>
          <li>
            <span>04</span> Authorized delivery enabled
          </li>
        </ol>
      </section>
    </>
  );
}

function Downloads() {
  const isFixture = fixtureRelease.signatureState === "fixture-not-installable";
  return (
    <section className="panel" aria-labelledby="downloads-title">
      <div className="panel-heading">
        <div>
          <p className="section-tag">RELEASE VAULT</p>
          <h1 id="downloads-title">Downloads</h1>
        </div>
        <StatusPill tone="warning">Development only</StatusPill>
      </div>
      <div className="release-card">
        <div>
          <span className="release-version">{fixtureRelease.version}</span>
          <h2>Pipeline validation fixture</h2>
          <p>
            This record exercises manifest and portal behavior. It contains no
            application code and cannot be installed.
          </p>
        </div>
        <dl className="release-facts">
          <div>
            <dt>Channel</dt>
            <dd>{fixtureRelease.channel}</dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{fixtureRelease.status}</dd>
          </div>
          <div>
            <dt>Type</dt>
            <dd>{fixtureRelease.updateType}</dd>
          </div>
          <div>
            <dt>Package</dt>
            <dd>{fixtureRelease.packageSize}</dd>
          </div>
        </dl>
        <button className="button button-disabled" disabled={isFixture}>
          Download installer
        </button>
      </div>
      <p className="callout">
        <strong>Safety gate:</strong> Only Authenticode-verified, approved
        artifacts can produce a customer download link.
      </p>
    </section>
  );
}

function Documentation() {
  const [query, setQuery] = useState("");
  const matchingDocuments = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return documents;
    return documents.filter((document) =>
      `${document.category} ${document.title} ${document.summary}`
        .toLowerCase()
        .includes(normalized),
    );
  }, [query]);

  return (
    <section className="panel" aria-labelledby="docs-title">
      <div className="panel-heading docs-heading">
        <div>
          <p className="section-tag">KNOWLEDGE CENTER</p>
          <h1 id="docs-title">Documentation</h1>
          <p>
            Version-aware operational guidance for connected and disconnected
            environments.
          </p>
        </div>
        <label className="search-field">
          <span>Search documentation</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search titles and topics"
          />
        </label>
      </div>
      <div className="docs-toolbar">
        <span>Documentation version</span>
        <button className="select-button" type="button">
          Foundation draft <span aria-hidden="true">⌄</span>
        </button>
      </div>
      <div className="doc-grid">
        {matchingDocuments.map((document) => (
          <article className="doc-card" key={document.title}>
            <span>{document.category}</span>
            <h2>{document.title}</h2>
            <p>{document.summary}</p>
            <StatusPill
              tone={document.status === "Foundation" ? "ok" : "neutral"}
            >
              {document.status}
            </StatusPill>
          </article>
        ))}
      </div>
      {matchingDocuments.length === 0 && (
        <p className="empty-state">
          No foundation documents match that search.
        </p>
      )}
    </section>
  );
}

function Placeholder({ title }: { title: NavigationItem }) {
  const messages: Record<
    Exclude<NavigationItem, "Overview" | "Downloads" | "Documentation">,
    string
  > = {
    Licenses:
      "Signed offline entitlements and installation limits will appear here after the licensing policy is approved.",
    Organization:
      "Tenant users, roles, identity providers, domains, and administrative history will be managed here.",
    Billing:
      "Stripe test-mode subscription and contract status will be connected without storing payment card data in AWS.",
    Support:
      "Support requests, verified diagnostic bundles, known issues, and escalation guidance will live here.",
  };
  return (
    <section className="panel placeholder" aria-labelledby="placeholder-title">
      <p className="section-tag">FOUNDATION BOUNDARY</p>
      <h1 id="placeholder-title">{title}</h1>
      <p>{messages[title as keyof typeof messages]}</p>
      <StatusPill>Not connected</StatusPill>
    </section>
  );
}

export default function App() {
  const [active, setActive] = useState<NavigationItem>("Overview");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [runtimeConfig, setRuntimeConfig] = useState<RuntimeConfig | null>(
    null,
  );
  const [session, setSession] = useState<PortalSession | null>(null);
  const [authStatus, setAuthStatus] = useState("Loading portal configuration");

  useEffect(() => {
    void (async () => {
      try {
        const config = await loadRuntimeConfig();
        setRuntimeConfig(config);
        if (!config.authEnabled) {
          setAuthStatus("Local development fixture");
          return;
        }
        const nextSession = await completeLogin(config);
        setSession(nextSession);
        if (!nextSession) {
          setAuthStatus("Authentication required");
          return;
        }
        const context = await loadPortalContext(config, nextSession);
        setAuthStatus(`Tenant ${context.tenantId}`);
      } catch (error) {
        setAuthStatus(
          error instanceof Error
            ? error.message
            : "Portal authentication failed",
        );
      }
    })();
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <button
          className="mobile-toggle"
          type="button"
          aria-expanded={mobileOpen}
          aria-controls="portal-navigation"
          onClick={() => setMobileOpen((open) => !open)}
        >
          <span className="sr-only">Toggle navigation</span>
          <span aria-hidden="true">☰</span>
        </button>
        <div className="brand" aria-label="Air Gap Intel customer portal">
          <span className="brand-mark" aria-hidden="true">
            AGI
          </span>
          <span>
            <strong>AIR GAP INTEL</strong>
            <small>Customer portal</small>
          </span>
        </div>
        <div className="account-controls">
          <div className="environment">
            <span>
              {runtimeConfig?.environment === "dev" ? "DEV" : "LOCAL"}
            </span>{" "}
            {session?.email ?? authStatus}
          </div>
          {runtimeConfig?.authEnabled && !session && (
            <button
              className="account-button"
              type="button"
              onClick={() => void beginLogin(runtimeConfig)}
            >
              Sign in
            </button>
          )}
          {runtimeConfig?.authEnabled && session && (
            <button
              className="account-button"
              type="button"
              onClick={() => signOut(runtimeConfig)}
            >
              Sign out
            </button>
          )}
        </div>
      </header>

      <aside
        className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}
        id="portal-navigation"
      >
        <nav aria-label="Portal navigation">
          {navigation.map((item) => (
            <button
              key={item}
              type="button"
              className={active === item ? "active" : ""}
              aria-current={active === item ? "page" : undefined}
              onClick={() => {
                setActive(item);
                setMobileOpen(false);
              }}
            >
              <span className="nav-signal" aria-hidden="true" />
              {item}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span>Signed in as</span>
          <strong>{session?.email ?? "Local development fixture"}</strong>
          <small>{authStatus}</small>
        </div>
      </aside>

      <main id="main-content" className="content" tabIndex={-1}>
        {active === "Overview" && <Overview />}
        {active === "Downloads" && <Downloads />}
        {active === "Documentation" && <Documentation />}
        {!(
          ["Overview", "Downloads", "Documentation"] as NavigationItem[]
        ).includes(active) && <Placeholder title={active} />}
      </main>
    </div>
  );
}
